'use client'

import { useState, useEffect, useCallback } from 'react';
import { getFavorites } from '@nusrif/services';
import { Favorite } from '@nusrif/types';

interface UseFavoritesReturn {
  favorites: Favorite[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching user favorites
 */
export const useFavorites = (): UseFavoritesReturn => {
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFavorites = useCallback(async (showLoader: boolean = true) => {
    if (showLoader) setIsLoading(true);
    setError(null);

    try {
      const response = await getFavorites();

      if (response.success) {
        setFavorites(response.data);
      } else {
        setError(response.message || 'Failed to fetch favorites');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching favorites');
    } finally {
      if (showLoader) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFavorites(true);
  }, [fetchFavorites]);

  return {
    favorites,
    isLoading,
    error,
    refetch: () => fetchFavorites(false)
  };
}; 